import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import type {JOB_INFORMATION} from 'src/types/crm';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  ButtonTabs,
  ButtonTab,
} from '@spaced-out/ui-design-system/lib/components/ButtonTabs';
import {renderAsHTML} from 'src/components/crm/lib/common';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {
  BodyMedium,
  BodySmallBold,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {classify} from 'src/utils/classify';
import css from './job-description.css';


export const JobDescription = ({
  jobInfo,
  hideExternalJD = false,
}: {
  jobInfo: JOB_INFORMATION;
  hideExternalJD?: boolean;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [selectedJobDescriptionType, setSelectedJobDescriptionType] =
    React.useState('externalJD');
  const [isExpanded, setIsExpanded] = React.useState(false);
  const DESCRIPTION_WORD_LIMIT = 800;
  const JOB_DETAILS_TABS = [
    {
      label: t('External_JD', 'External JD'),
      type: 'externalJD',
    },
    {
      label: t('Internal_JD', 'Internal JD'),
      type: 'internalJD',
    },
  ];
  const renderDescription = (
    description: string,
    skills: undefined | string,
    isExternal: boolean,
  ) => {
    if (!description && !skills) {
      return (
        <EmptyState
          title={t(
            'No_Job_Description_available',
            'No Job Description available',
          )}
          description={`${t(
            'What_are_you_hiring_for',
            'What are you hiring for',
          )}?`}
          imageVariant="data"
        />
      );
    }

    const descriptionHTML = description;
    const skillsHTML = skills;

    return (
      <div className={css.descriptionContainer}>
        <BodyMedium className={css.jobDescription}>
          <div
            className={classify(
              descriptionHTML === ''
                ? css.jobDescriptionEmptyContent
                : css.jobDescriptionContent,
              {
                [css.collapsed]:
                  !isExpanded && description.length > DESCRIPTION_WORD_LIMIT,
              },
            )}
          >
            {descriptionHTML !== '' && (
              <>
                <BodySmallBold color="secondary">
                  {t('JOB_DESCRIPTION', 'JOB DESCRIPTION')}
                </BodySmallBold>
                <div
                  className={css.richTextEditor}
                  dangerouslySetInnerHTML={{__html: description}}
                />
              </>
            )}
          </div>
          {(isExpanded || description.length <= DESCRIPTION_WORD_LIMIT) &&
            skills && (
              <div className={css.jobDescriptionContent}>
                <BodySmallBold color="secondary">
                  {t('SKILLS_AND_EXPERTISE', 'SKILLS AND EXPERTISE')}
                </BodySmallBold>
                <div
                  className={css.richTextEditor}
                  // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string | TrustedHTML'.
                  dangerouslySetInnerHTML={{__html: skillsHTML}}
                />
              </div>
            )}
        </BodyMedium>
        {description.length > DESCRIPTION_WORD_LIMIT && (
          <Link
            onClick={() => setIsExpanded(!isExpanded)}
            className={css.showMoreButton}
            underline={false}
          >
            {isExpanded
              ? t('Show_Less', 'Show Less')
              : t('Show_More', 'Show More')}
          </Link>
        )}
      </div>
    );
  };

  const JobDescriptionTabItems = () => (
    <ButtonTabs
      size="small"
      onButtonTabSelect={(type) => {
        setSelectedJobDescriptionType(type);
        setIsExpanded(false);
      }}
      selectedButtonTabId={selectedJobDescriptionType}
      classNames={{wrapper: css.buttonTabs}}
    >
      {JOB_DETAILS_TABS.map(({label, type}) => (
        <ButtonTab id={type} key={type}>
          {label}
        </ButtonTab>
      ))}
    </ButtonTabs>
  );
  return (
    <>
      <div className={css.jobDescriptionContainer}>
        {(jobInfo.descriptionExternal || jobInfo.descriptionInternal) &&
          !jobInfo.internalDescSameAsExternal &&
          !hideExternalJD && (
            <div className={css.jobDescriptionTabs}>
              <JobDescriptionTabItems />
            </div>
          )}

        <CardContent>
          {selectedJobDescriptionType === 'externalJD' &&
            renderDescription(
              jobInfo.descriptionExternal,
              jobInfo.skillsAndExpertiseExternal,
              true,
            )}
          {selectedJobDescriptionType === 'internalJD' &&
            renderDescription(
              jobInfo.descriptionInternal,
              jobInfo.skillsAndExpertiseInternal,
              false,
            )}
        </CardContent>
      </div>
    </>
  );
};
